/**
 * Sticky feature-flag snapshots — one frozen answer per flag, per document.
 *
 * PostHog picks a variant by hashing the `distinct_id`. A brand-new shopper
 * reads a flag while ANONYMOUS and is `identify()`d seconds later, so the hash
 * input changes and the SAME person can land in a different arm on a later
 * surface. Measured in prod on 2026-08-27: ~10% of registrations had their
 * DUP-1903 arm change mid-session, and 690 shoppers were asked to subscribe to
 * the newsletter twice — once under each arm.
 *
 * OUR POSTHOG CONFIG IS CORRECT. `ensure_experience_continuity` (the "persist
 * flag across authentication" setting, which exists precisely to stop this) is
 * enabled on the flag, `bucketing_identifier` is the default `distinct_id`, and
 * the variants are right. There is no setting left to turn on. Continuity simply
 * does not hold — a known OPEN posthog-js bug:
 *
 *   https://github.com/PostHog/posthog-js/issues/2623
 *   "Feature flag value changes after identify, `Flag persistence` is enabled"
 *   (opened Nov 2025, still open as of Aug 2026)
 *
 * PostHog's own docs are blunt about the cost — "For Experiments, this is data
 * corruption: you experienced 'test' but may now be counted in 'control'"
 * (posthog.com/docs/feature-flags/stable-identity-for-flags). Their recommended
 * fix is a "stable ID from first touch", i.e. an app-wide identity change, and
 * bootstrapping does NOT help because "the next complete /flags response
 * replaces the bootstrapped values". So we freeze it ourselves.
 *
 * IN MEMORY ONLY, deliberately — not sessionStorage. Note this is a PREFERENCE,
 * not a safety requirement: `getServerSnapshot` (below) would make a
 * storage-backed read hydration-safe too, since React uses it for the server
 * render AND the hydration render. The reasons are narrower than that:
 *
 *   1. IT ISN'T NEEDED — nothing reloads the document in the window that
 *      matters. Verified across every sign-in path as of 2026-08-27:
 *        · google / apple → `signInWithPopup` with an explicit
 *          `browserPopupRedirectResolver`, so a blocked popup throws
 *          `auth/popup-blocked` rather than silently redirecting
 *        · email → `signInWithEmailAndPassword`, in-modal
 *        · phone → `signInWithPhoneNumber`, in-modal
 *      There is no `signInWithRedirect`, no `getRedirectResult`, no email-LINK
 *      sign-in, and no navigation in `AuthContent` after auth. So sign-up →
 *      onboarding never crosses a document load on ANY path.
 *   2. LIFETIME CORRECTNESS. A persisted arm would outlive the consent decision
 *      it explains. `pendingMarketingConsentStore` is a deliberately
 *      non-persisted module singleton for exactly this reason — persisting a
 *      pre-account choice caused a real cross-account leak.
 *   3. Storage can throw (private mode, partitioned contexts). Memory can't.
 *
 * ⚠️ TRIGGER TO REVISIT: if a redirect-based sign-in is ever added — e.g.
 * `signInWithRedirect` as an iOS/in-app-browser fallback for blocked popups —
 * then the document DOES die mid-flow and memory is no longer sufficient. Move
 * this store to sessionStorage behind the same `useSyncExternalStore` shape
 * (keeping `getServerSnapshot` returning `undefined`), and add a short TTL so a
 * stale arm can't be inherited later.
 *
 * A manual mid-flow reload still crosses arms today. Accepted: it's rare, and
 * the experiment's primary funnel starts at `Registration Completed`.
 *
 * BROWSER-ONLY: `freezeStickyFlag` must never write server-side. Node module
 * singletons are shared across concurrent SSR requests, so one server write
 * would leak a shopper's arm onto every in-flight response — the same hazard
 * `signupWallVariantSnapshot` documents. The guard is in the setter, not the
 * caller, so no future caller can get it wrong.
 *
 * Read through `useIsFeatureOn(flag, { sticky: true })`, never directly.
 * Tests should call `resetStickyFlags()` in `beforeEach` to stay hermetic.
 */

export interface StickyFlagSnapshot {
  /** `useFeatureFlagEnabled` at freeze time. Never `undefined`. */
  featureOn: boolean
  /**
   * `useFeatureFlagVariantKey` at freeze time. A variant STRING for a
   * multivariate flag, a BOOLEAN for a plain boolean flag — posthog-js returns
   * the boolean value for non-multivariate flags. Never `undefined`.
   */
  variant: string | boolean
  /**
   * Frozen alongside the variant so a payload can never describe a different
   * arm than the variant it shipped with.
   */
  payload: unknown
}

const snapshots = new Map<string, StickyFlagSnapshot>()
const listeners = new Set<() => void>()

/** `useSyncExternalStore` subscribe for sticky call sites. */
export const subscribeStickyFlags = (
  onStoreChange: () => void
): (() => void) => {
  listeners.add(onStoreChange)
  return () => {
    listeners.delete(onStoreChange)
  }
}

/**
 * Stable no-op subscribe for NON-sticky call sites. Must be module-level (not an
 * inline arrow) or `useSyncExternalStore` resubscribes on every render.
 */
export const noopSubscribeStickyFlags = (): (() => void) => () => {}

export const getStickyFlag = (
  feature: string
): StickyFlagSnapshot | undefined => snapshots.get(feature)

/**
 * The `getServerSnapshot` contract, and the read for non-sticky call sites.
 * Returning `undefined` on the server AND on the hydration render is what makes
 * the DUP-1766 hydration mismatch impossible rather than merely unlikely.
 */
export const readNoStickyFlag = (): StickyFlagSnapshot | undefined => undefined

/**
 * Freeze a flag's resolved value for the rest of this document.
 *
 * FIRST WRITE WINS — several components mount the same hook, and a later
 * instance must not clobber the value an earlier one already committed to. Same
 * "only upgrade, never downgrade" guard as `useExtensionState`.
 *
 * Callers must never pass an undefined value: an absent or DRAFT flag reads
 * `undefined` forever (not `false`), and latching that would make a temporary
 * absence permanent. `useIsFeatureOn` enforces it.
 */
export const freezeStickyFlag = (
  feature: string,
  snapshot: StickyFlagSnapshot
): void => {
  if (typeof window === 'undefined') return
  if (snapshots.has(feature)) return
  snapshots.set(feature, snapshot)
  for (const listener of listeners) listener()
}

/**
 * Drop every frozen value. Called on sign-out so a logout → sign-in-as-someone-
 * else in the same tab doesn't inherit the previous shopper's arm. Also the test
 * helper — call it in `beforeEach`.
 *
 * NOTIFIES rather than clearing `listeners`. That Set is owned by each mounted
 * hook's own subscribe/unsubscribe lifecycle, so clearing it would silently drop
 * the subscription of anything still mounted: it would never be woken again, and
 * would keep rendering the previous shopper's arm until some unrelated re-render
 * happened to pick up the change — the precise leak this reset exists to stop.
 * Notifying instead makes every live subscriber re-read immediately and get
 * `undefined` back.
 */
export const resetStickyFlags = (): void => {
  snapshots.clear()
  for (const listener of listeners) listener()
}
